import {
  getAllStudentsForTeacher, getAllStudentsForTeacherCount,
  getClass,
  getClassList,
  getClassInvites,
  getClassStudents,
  getClassTeachers,
  getStudentClassList,
} from '../../../../common/api';
import { loadState } from '../../localstorage';
import _ from 'lodash';
import Vue from 'vue';
import Utils from 'common/Utils';

const persistedState = loadState() || {};

const state = persistedState.classes || {
  teacher_id: null,
  classes_list: [],
  classes_by_ids: {},
  current_class: null,
  all_students: [],
  all_students_count: -1,
  classes_by_student: {},
  is_class_loading: false,
  invites_list: []
}

const getters = {
}

const mutations = {
  SET_TEACHER_ID(state, teacher_id) {
    state.teacher_id = teacher_id
  },
  SET_STUDENTS(state, students) {
    state.all_students = students;
  },
  SET_STUDENTS_COUNT(state, student_count) {
    state.all_students_count = student_count
  },
  SET_CLASSES_LIST(state, classes) {
    state.classes_list = classes.map(clazz => ({ ...clazz, classId: clazz.id}));
  },
  SET_CLASS(state, clazz) {
    Vue.set(state.classes_by_ids, clazz.id, clazz)
  },
  SET_CURRENT_CLASS(state, classObj) {
    state.current_class = classObj
  },
  SET_STUDENT_CLASSES(state, {student_id, classes}) {
    state.classes_by_student[student_id] = [...classes]
  },
  SET_INVITES_LIST(state, invites) {
	state.invites_list = invites;
  }
}

const actions = {

  /***
   * Gets all students for a given teacher
   * @param commit
   * @param teacher_id - the teacher id
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<*>}
   */
  async getAllStudents({commit}, {teacher_id=state.teacher_id, alwaysFetchFromServer=false}) {
    if (!_.isEmpty(state.all_students) && !alwaysFetchFromServer) return state.all_students;
    const students = await getAllStudentsForTeacher(teacher_id)
    _.forEach(students, item => { item.studentId = item.id })
    commit('SET_STUDENTS', students);
    commit('SET_STUDENTS_COUNT', students.length)
    return state.all_students;
  },

  /***
   * Gets all students for a given teacher
   * @param commit
   * @param teacher_id - the teacher id
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<*>}
   */
  async getAllStudentsCount({commit}, {teacher_id=state.teacher_id, alwaysFetchFromServer=false}) {
    if (state.all_students_count > -1 && !alwaysFetchFromServer) return state.all_students_count;
    const {count} = await getAllStudentsForTeacherCount(teacher_id)
    commit('SET_STUDENTS_COUNT', count);
    return state.all_students_count;
  },

  /***
   * Gets all classes for a given teacher using the class list api (partial data on the classes)
   * @param commit
   * @param teacher_id - the teacher id
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<PartialClassObject>}
   */
  async getClassesList({commit}, {teacher_id=state.teacher_id, withStudents=false, alwaysFetchFromServer=false}) {
    if (!_.isEmpty(state.classes_list) && !alwaysFetchFromServer) return state.classes_list;
	const studentsRequets = []
    const classes_list = await getClassList(teacher_id)
    _.forEach(classes_list, clazz => {
      clazz._isPartial = true
      addBackwardsCompatibilityFields(clazz)

	  if (withStudents) {
		studentsRequets.push(getClassStudents(clazz.classId))
	  }
    })

	if (withStudents) {
	  const studentsResponses = await Promise.all(studentsRequets)
	  _.forEach(classes_list, (clazz, index) => {
	    const students_list = studentsResponses[index]
		addStudentsBackwardsCompatibility(students_list)
		clazz.students_list = students_list
	  })
	}
    commit('SET_CLASSES_LIST', classes_list);
    return state.classes_list;
  },

  /***
   * Gets student's classes using the class list api (partial data on the classes)
   * @param commit
   * @param student_id - the student id
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<PartialClassObject>}
   */
  async getStudentClasses({commit}, {student_id}, alwaysFetchFromServer=false) {
    if (!_.isEmpty(state.classes_by_student[student_id]) && !alwaysFetchFromServer) return state.classes_by_student[student_id];
    const classes_list = await getStudentClassList(student_id)
    _.forEach(classes_list, clazz => {
      clazz._isPartial = true
      addBackwardsCompatibilityFields(clazz)
    })
      commit('SET_STUDENT_CLASSES', {student_id, classes: classes_list});
    return state.classes_by_student[student_id];
  },

  /***
   * Gets the a full class object for a class id
   * @param commit
   * @param class_id - the id of the class we want to fetch
   * @param withStudents - if should also fetch all the students for that class (class.students_list) for this class
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<FullClassObject>}
   */
  async getClass({state, commit, dispatch}, {classId, withStudents =false, withTeachers=true, alwaysFetchFromServer = false}) {
    let clazz = state.classes_by_ids[classId];
    let shouldFetchFromState = clazz && !alwaysFetchFromServer
    if (!shouldFetchFromState) {
      const fullClassObj = await getClass(classId);
      clazz = _.assign(fullClassObj, clazz)
      addBackwardsCompatibilityFields(clazz)
      commit('SET_CLASS', clazz)
    }
    const shouldFetchStudents = withStudents && (alwaysFetchFromServer || !clazz.students_list)
    if (shouldFetchStudents) {
      await dispatch('getStudentsForClass', {classId, alwaysFetchFromServer: true})
    }
    const shouldFetchTeachers = withTeachers && (!clazz.teachers_list || alwaysFetchFromServer)
    if (shouldFetchTeachers) {
      await dispatch('getTeachersForClass', {classId, alwaysFetchFromServer: true})
    }
    return state.classes_by_ids[classId]
  },

  /***
   * Gets the full student list for a class
   * @param commit
   * @param class_id - the id of the class we want to fetch
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<List<Students>}
   */
  async getStudentsForClass({commit}, {classId, alwaysFetchFromServer = false}) {
    const clazz = state.classes_by_ids[classId];
    const shouldFetchFromState = !alwaysFetchFromServer && clazz && clazz.students_list;
    if (!shouldFetchFromState) {
      const students_list = await getClassStudents(classId);
      _.forEach(students_list, student => student.studentId = student.id)
      addStudentsBackwardsCompatibility(students_list)
      commit('SET_CLASS', {...clazz, students_list})
    }
	return state.classes_by_ids[classId]
  },

  /***
   * Gets the full teacher list for a class
   * @param commit
   * @param class_id - the id of the class we want to fetch
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<List<Students>}
   */
  async getTeachersForClass({commit}, {classId, alwaysFetchFromServer = false}) {
    const clazz = state.classes_by_ids[classId];
    const shouldFetchFromState = !alwaysFetchFromServer && clazz && clazz.teachers_list;
    let teachers_list = [];
    if (!shouldFetchFromState) {
      teachers_list = await getClassTeachers(classId);
      _.forEach(teachers_list, teacher => teacher.teacherId = teacher.id)
      commit('SET_CLASS', {...clazz, teachers_list})
    }
    return clazz ? clazz.teachers_list : teachers_list;
  },

  /***
   * Gets the invite list for a classes for teacher
   * @param commit
   * @param alwaysFetchFromServer - if set to true will always fetch from the server, otherwise will try taking from the state first
   * @returns {Promise<List<Invites>}
   */
  async getInvitesList({state, commit}, {alwaysFetchFromServer=false}) {
	if (!_.isEmpty(state.invites_list) && !alwaysFetchFromServer) return state.invites_list;
	const invites_list = await getClassInvites();
	commit('SET_INVITES_LIST', invites_list);
	return state.invites_list;
  }
};

function addStudentsBackwardsCompatibility(students) {
  _.forEach(students, student => {
	  student.studentId = student.id;
      student.firstName = student.first_name;
      student.lastName = student.last_name;
	  student.displayName = Utils.getDisplayName(student);
  })
}

function addBackwardsCompatibilityFields(clazz) {
  if (clazz.code) {
    clazz.class_code = clazz.code
  } else {
    clazz.code = clazz.class_code
  }
  if (clazz.class_setting) {
    clazz.classSettingObject = clazz.class_setting
  }
  clazz.classId = clazz.id
  return clazz;
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
